import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { fadeInUp } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';

const ProcessFlow = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  const containerRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [progressWidth, setProgressWidth] = useState(0);

  const processSteps = [
    {
      id: 'billet',
      title: 'Billet Inspection',
      subtitle: 'Quality Verification',
      description: 'Raw steel billets undergo comprehensive inspection including chemical composition verification, surface quality assessment, and dimensional checking.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      details: ['Chemical analysis', 'Surface inspection', 'Dimensional verification', 'Grade certification'],
      temperature: 'Ambient',
      duration: '15-30 min'
    },
    {
      id: 'heating',
      title: 'Controlled Heating',
      subtitle: 'Temperature Optimization',
      description: 'Billets are heated in controlled furnaces to optimal rolling temperature with precise temperature profiles to ensure uniform heating throughout.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
        </svg>
      ),
      details: ['Uniform heating', 'Temperature monitoring', 'Soaking time control', 'Scale removal'],
      temperature: '1200°C',
      duration: '2-4 hours'
    },
    {
      id: 'rolling',
      title: 'Precision Rolling',
      subtitle: 'Shape Formation',
      description: 'Hot billets pass through multiple rolling stands with precise pressure and speed control to achieve the desired cross-sectional shape and dimensions.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      details: ['Multi-stand rolling', 'Pressure control', 'Speed optimization', 'Shape accuracy'],
      temperature: '1000-1200°C',
      duration: '3-5 min'
    },
    {
      id: 'cooling',
      title: 'Controlled Cooling',
      subtitle: 'Metallurgy Control',
      description: 'Systematic cooling process using controlled air flow and water sprays to achieve optimal mechanical properties and grain structure.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
        </svg>
      ),
      details: ['Controlled cooling rate', 'Air blast cooling', 'Water spray systems', 'Grain refinement'],
      temperature: '200-400°C',
      duration: '10-20 min'
    },
    {
      id: 'shearing',
      title: 'Cold Shearing',
      subtitle: 'Length Precision',
      description: 'Cooled steel bars are cut to precise lengths using high-capacity shears with automated length control for exact specifications.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M19 4h1a1 1 0 011 1v1M5 4H4a1 1 0 00-1 1v1m0 4v6m20-6v6m0 4v1a1 1 0 01-1 1h-1m-6 0H9m-4 0H4a1 1 0 01-1-1v-1" />
        </svg>
      ),
      details: ['Automated cutting', 'Length accuracy', 'Clean cut edges', 'Bundle preparation'],
      temperature: 'Ambient',
      duration: '2-3 min'
    },
    {
      id: 'qa',
      title: 'Quality Testing',
      subtitle: 'Final Verification',
      description: 'Comprehensive testing including tensile strength, yield strength, elongation, and dimensional accuracy to ensure specification compliance.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      details: ['Tensile testing', 'Dimensional check', 'Visual inspection', 'Certification'],
      temperature: 'Ambient',
      duration: '30-60 min'
    },
    {
      id: 'dispatch',
      title: 'Logistics & Dispatch',
      subtitle: 'Delivery Preparation',
      description: 'Certified products are bundled, tagged, and prepared for dispatch with complete documentation and traceability records.',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      details: ['Bundle tagging', 'Documentation', 'Loading preparation', 'Dispatch scheduling'],
      temperature: 'Ambient',
      duration: '1-2 hours'
    }
  ];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const stepWidth = container.scrollWidth / processSteps.length;
      const newActiveStep = Math.round(scrollLeft / stepWidth);
      setActiveStep(Math.min(newActiveStep, processSteps.length - 1));
    };

    // Initial calculation
    setTimeout(() => {
      handleScroll();
    }, 100);

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [processSteps.length]);

  // Update progress bar based on active step
  useEffect(() => {
    const stepProgress = ((activeStep + 1) / processSteps.length) * 100;
    setProgressWidth(stepProgress);
  }, [activeStep, processSteps.length]);

  return (
    <Section id="process" background="particles" containerSize="full" className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-16">
          <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
            Our <span className="text-primary-400">Process</span>
          </h2>
          <p className={`text-body-lg ${colors.text.secondary} max-w-3xl mx-auto leading-relaxed mb-4 lg:mb-8`}>
            From raw billet to finished product, our systematic approach ensures 
            consistent quality and optimal mechanical properties at every stage.
          </p>
          
          {/* Progress Bar */}
          <div className={`w-full max-w-2xl mx-auto ${colors.bg.muted} rounded-full h-2 overflow-hidden transition-colors duration-300`}>
            <div
              className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full transition-all duration-300"
              style={{ width: `${progressWidth}%` }}
            />
          </div>
        </div>

        {/* Active Step Display */}
        <motion.div
          className="text-center mb-6 lg:mb-12"
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card glass={true} className="inline-block p-4 lg:p-6">
            <div className="flex items-center space-x-3 lg:space-x-4">
              <div className="text-primary-400">
                <div className="w-8 h-8 lg:w-12 lg:h-12">
                  {React.cloneElement(processSteps[activeStep]?.icon, { 
                    className: "w-8 h-8 lg:w-12 lg:h-12" 
                  })}
                </div>
              </div>
              <div className="text-left">
                <h3 className={`text-lg lg:text-xl font-semibold ${colors.text.primary}`}>
                  Step {activeStep + 1}: {processSteps[activeStep]?.title}
                </h3>
                <p className="text-primary-400 text-xs lg:text-sm font-medium">
                  {processSteps[activeStep]?.subtitle}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Horizontal Scrolling Process Steps */}
      <div className="relative">
        {/* Left scroll button */}
        <motion.button
          className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-primary-400 text-black rounded-full flex items-center justify-center shadow-lg hover:bg-primary-300 transition-colors md:w-12 md:h-12"
          onClick={() => {
            const container = containerRef.current;
            if (container) {
              // Calculate previous step
              const prevStep = Math.max(activeStep - 1, 0);
              setActiveStep(prevStep);
              
              // Auto-snap to show the progress bar, step indicator, and the card
              container.scrollBy({ left: -320, behavior: 'smooth' });
              
              // Scroll the main page to ensure progress bar is at top
              setTimeout(() => {
                if (window.innerWidth < 1024) { // Only on mobile
                  const sectionElement = document.getElementById('process');
                  if (sectionElement) {
                    // Find the progress bar element
                    const progressContainer = sectionElement.querySelector('.w-full.max-w-2xl.mx-auto');
                    if (progressContainer) {
                      const progressRect = progressContainer.getBoundingClientRect();
                      const scrollOffset = progressRect.top + window.pageYOffset - 80; // 80px from top for better visibility
                      window.scrollTo({ top: scrollOffset, behavior: 'smooth' });
                    }
                  }
                }
              }, 300);
            }
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Scroll left"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>

        {/* Right scroll button */}
        <motion.button
          className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-primary-400 text-black rounded-full flex items-center justify-center shadow-lg hover:bg-primary-300 transition-colors md:w-12 md:h-12"
          onClick={() => {
            const container = containerRef.current;
            if (container) {
              // Calculate next step
              const nextStep = Math.min(activeStep + 1, processSteps.length - 1);
              setActiveStep(nextStep);
              
              // Auto-snap to show the progress bar, step indicator, and the card
              container.scrollBy({ left: 320, behavior: 'smooth' });
              
              // Scroll the main page to ensure progress bar is at top
              setTimeout(() => {
                if (window.innerWidth < 1024) { // Only on mobile
                  const sectionElement = document.getElementById('process');
                  if (sectionElement) {
                    // Find the progress bar element
                    const progressContainer = sectionElement.querySelector('.w-full.max-w-2xl.mx-auto');
                    if (progressContainer) {
                      const progressRect = progressContainer.getBoundingClientRect();
                      const scrollOffset = progressRect.top + window.pageYOffset - 80; // 80px from top for better visibility
                      window.scrollTo({ top: scrollOffset, behavior: 'smooth' });
                    }
                  }
                }
              }, 300);
            }
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Scroll right"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </motion.button>

        <div
          ref={containerRef}
          className="overflow-x-auto scrollbar-hide pb-8 cursor-grab active:cursor-grabbing"
          style={{ 
            scrollBehavior: 'smooth',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
          onMouseDown={(e) => {
            const container = e.currentTarget;
            let isDown = true;
            let startX = e.pageX - container.offsetLeft;
            let scrollLeft = container.scrollLeft;

            const handleMouseMove = (e) => {
              if (!isDown) return;
              e.preventDefault();
              const x = e.pageX - container.offsetLeft;
              const walk = (x - startX) * 2;
              container.scrollLeft = scrollLeft - walk;
            };

            const handleMouseUp = () => {
              isDown = false;
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        >
          <div className="flex space-x-8 px-4 sm:px-6 lg:px-8 min-w-max">
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                className="flex-shrink-0 w-80"
              >
                <ProcessStepCard 
                  step={step} 
                  index={index} 
                  isActive={index === activeStep}
                  colors={colors}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Dots */}
      <div className="flex justify-center space-x-3 mt-8">
        {processSteps.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              const container = containerRef.current;
              if (container) {
                const stepWidth = container.scrollWidth / processSteps.length;
                container.scrollTo({
                  left: stepWidth * index,
                  behavior: 'smooth'
                });
              }
            }}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === activeStep 
                ? 'bg-primary-400 scale-125' 
                : `${colors.bg.muted} hover:${colors.text.muted}`
            }`}
          />
        ))}
      </div>
    </Section>
  );
};

const ProcessStepCard = ({ step, index, isActive, colors }) => {
  return (
    <Card 
      className={`h-full p-8 transition-all duration-500 ${
        isActive 
          ? `${colors.bg.card} border-primary-400/50 shadow-magnetic` 
          : `${colors.bg.muted} ${colors.border.default}`
      }`}
    >
      {/* Header with Icon and Step Number - Horizontal Layout */}
      <div className="flex items-center justify-between mb-6">
        {/* Icon */}
        <div className={`transition-colors duration-300 ${
          isActive ? 'text-primary-400' : colors.text.muted
        }`}>
          {step.icon}
        </div>

        {/* Step Number */}
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
          isActive 
            ? 'bg-primary-400 text-black' 
            : `${colors.bg.muted} ${colors.text.secondary}`
        }`}>
          {index + 1}
        </div>
      </div>

      {/* Title and Subtitle */}
      <h3 className={`text-xl font-semibold ${colors.text.primary} mb-2`}>{step.title}</h3>
      <p className="text-primary-400 text-sm font-medium mb-4">{step.subtitle}</p>

      {/* Description */}
      <p className={`${colors.text.secondary} text-sm leading-relaxed mb-6`}>
        {step.description}
      </p>

      {/* Process Details */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className={colors.text.muted}>Temperature:</span>
            <div className={`${colors.text.primary} font-medium`}>{step.temperature}</div>
          </div>
          <div>
            <span className={colors.text.muted}>Duration:</span>
            <div className={`${colors.text.primary} font-medium`}>{step.duration}</div>
          </div>
        </div>

        {/* Key Points */}
        <div>
          <h5 className={`${colors.text.primary} font-medium mb-2 text-sm`}>Key Points:</h5>
          <ul className="space-y-1">
            {step.details.map((detail, idx) => (
              <motion.li
                key={detail}
                className={`flex items-center ${colors.text.muted} text-xs`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 flex-shrink-0"></div>
                {detail}
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {/* Connection Line */}
      {index < 6 && (
        <div className="absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-primary-400 to-primary-600 transform -translate-y-1/2 hidden lg:block">
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1">
            <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ProcessFlow;